// Tracks "has the current user already seen this welcome tour?".
//
// Source of truth: the authenticated user document (`user.tours_seen`) on the
// backend. This keeps the flag tied to the account (not the browser) so users
// don't get re-spammed after clearing cache, switching device, or letting a
// teammate share the same browser. We also mirror the value into the user
// blob in `localStorage` for synchronous reads from React effects without
// having to wait for an extra API round-trip.

import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Legacy localStorage prefix from when tours were tracked per-browser.
const LEGACY_KEY_PREFIX = 'allureWelcomeTourSeen:';

const readUser = () => {
  try {
    const raw = window.localStorage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeUser = (user) => {
  try {
    window.localStorage.setItem('user', JSON.stringify(user));
  } catch {
    // localStorage may be disabled; ignore
  }
};

const readToken = () => {
  try {
    return window.localStorage.getItem('token') || '';
  } catch {
    return '';
  }
};

export const hasSeenWelcomeTour = (key) => {
  if (!key) return true;
  const user = readUser();
  // No user means we can't decide yet — suppress the tour to avoid showing it
  // to logged-out visitors.
  if (!user) return true;
  const seen = Array.isArray(user.tours_seen) ? user.tours_seen : [];
  if (seen.includes(key)) return true;
  // One-time migration: respect the old per-browser flag the previous build
  // wrote so existing users don't see the tour pop up again post-upgrade.
  try {
    if (window.localStorage.getItem(`${LEGACY_KEY_PREFIX}${key}`) === '1') {
      const nextUser = { ...user, tours_seen: [...seen, key] };
      writeUser(nextUser);
      // Best-effort sync to backend so future devices stay quiet too.
      const token = readToken();
      if (token) {
        axios
          .post(
            `${API}/auth/me/tours-seen`,
            { key },
            { headers: { Authorization: `Bearer ${token}` } },
          )
          .catch(() => {});
      }
      return true;
    }
  } catch {
    // ignore
  }
  return false;
};

export const markWelcomeTourSeen = (key) => {
  if (!key) return;
  const user = readUser();
  if (user) {
    const seen = Array.isArray(user.tours_seen) ? user.tours_seen : [];
    if (!seen.includes(key)) {
      writeUser({ ...user, tours_seen: [...seen, key] });
    }
  }
  // Keep the legacy localStorage key in sync for any code that still reads it.
  try {
    window.localStorage.setItem(`${LEGACY_KEY_PREFIX}${key}`, '1');
  } catch {
    // ignore
  }
  const token = readToken();
  if (!token) return;
  // Fire-and-forget — the localStorage write above is the synchronous source
  // of truth for the current session.
  axios
    .post(
      `${API}/auth/me/tours-seen`,
      { key },
      { headers: { Authorization: `Bearer ${token}` } },
    )
    .catch(() => {});
};
